import ApiService from "./api-service";
import get from "lodash/get";

const CASH_AT_RISK = `
  cashAtRisk {
    cashAtRisk
    percentageChange
  }
`;

const MONTHLY_FORECAST = `
  monthlyForecast {
    predictedPaymentAmount
    monthlyPercChange
  }
`;

const ANNUAL_FORECAST = `
  annualForecast {
    annualForecast
    annualPercChange
  }
`;

const FORECASTING_QUERIES = Object.freeze({
  yearToDate: "yearToDate",
  cashAtRisk: CASH_AT_RISK,
  monthlyForecast: MONTHLY_FORECAST,
  annualForecast: ANNUAL_FORECAST,
});

const OrganisationService = {
  async get(shortCode) {
    const body = {
      query: `{
        organisation {
          _id
          sandbox
          cashFlowIntelligence {
            onboardingProcessCompleted
            onboardingProcessInitiated
            cashFlowForecastTrialEndDate
          }
          features {
            chaser_core
            stripeFee
            openapi
            customer_recipients_visible_labels
            nosync_invoice_pdf
            collections_provider
          }
          createdAt
          createdBy {
            _id
          }
          Addresses {
            AddressLine1
            AddressLine2
            AddressLine3
            AddressLine4
            AddressType
            City
            Region
            PostalCode
            Country
            AttentionTo
          }
          hasConfirmedDetails
          lastRefreshDate
          lastSyncDate
          ShortCode
          accountingSoftware
          integration
          noChaseUntil
          BaseCurrency
          CountryCode
          noChase
          OrganisationID
          Name
          name
          updating
          partner {
            subscriber {
              _id
            }
          }
          manager {
            _id
          }
          accessToken {
            expired
            type
            host
            port
            directory
            username
            region
            url
            db
            companyId
            userId
            certificateId
          }
          schedules {
            _id
            chasers {
              escalateSenders
              escalateRecipients
              channel
            }
            monthlyStatement {
              active
            }
            reminders {
              type
            }
          }
          subscription {
            _id
            isExpiredTrial
            isInTrial
            trialExpiryDate
            moduleSubscriptions {
              moduleName
              isInTrial
              trialEndsAt
            }
            isCancelled
            subscriptionService
            subscriber {
              _id
            }
            plan {
              title
              name
              features {
                invoices
                senders
                users
                templates
                schedules
                escalatedSenders
                escalatedRecipients
                domainVerification
                directEmail
                customerReport
                stripeDDPayment
                recommendedChasingTimes
                latePaymentIndicator
                creditReport
                creditMonitoring
                expectedPaymentDates
                invoiceDispute
                collections
                sms
                phoneCall
                callReminders
                customerTags
                manualBankTransfer
                inAppCalls
                callRecording
                tfpEmails
                autoCall
                feesAndDiscounts
                artificialIntelligence
                letter
                inPortalAdvert
                revenueForecasting
                emailIntegrationConnections
                paymentScore
                paymentPlans
                editSenders
              }
            }
            isSuspended
            isSubscribed
          }
          timezone
          users {
            id
            EmailAddress
            nylas {
              isConnected
              errors
              provider
              emailAddress
            }
          }
          scheduleTimes {
            day
            hour
          }
          thanksForPayingTimes {
            day
            hour
          }
          monthlyStatementTimes {
            day
            hour
          }
          contacts {
            hasMissingEmail
            hasMissingName
            chasingMissingEmailCount
          }
          creditReportAllowance
          minimumDaysBetweenChase {
            enabled
          }
          recommendedScheduleTimes {
            day
            hour
          }
          calculatedAnnualRevenue {
            value
            updatedAt
            currencyCode
          }
          customerPortal {
            paymentTimingAdjustments {
              latePaymentFees {
                feePolicy {
                  type
                  feeValue
                }
                overDueDays
                dateToUse
                interestChargedFrom
                applieFeesPolicy
                feeFrequency
                scheduleIdsToApply
                integrationFeesInformation {
                  account
                  integrationTaxType
                  itemCode
                  description
                }
                internalFeesInformation {
                  taxValue
                  taxType
                }
              }
              earlyPaymentDiscount {
                feeValue
                feePolicy
                daysBeforeToApplyDiscount
                dateToUse
                applieFeesPolicy
                feeFrequency
                scheduleIdsToApply
                integrationFeesInformation {
                  account
                  integrationTaxType
                  itemCode
                  description
                }
                internalFeesInformation {
                  taxValue
                  taxType
                }
              }
            }
            payments {
              status
              enabled
              provider
              accountId
              accountCode
              currencies {
                accountCode
                currency
                type
              }
              fees {
                type
                fixedFee
                variablePercentageFee
                reimbursementAccountCode
              }
            }
          }
          Subsidiaries {
            id
            name
            enabled
          }
        }
      }`,
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);

    if (!data.organisation) throw new Error("Organisation does not exist");
    return data.organisation;
  },
  async resumeChasing(shortCode) {
    const body = {
      query: `mutation {
        resume {
          ShortCode
        }
      }`,
    };
    const { data: result } = await ApiService.orgEndpoint(shortCode, {
      extractResponseData: false,
    }).post("/", { ...body });
    const { data, errors } = result;
    if (errors && errors.length) {
      throw errors;
    }
    return data.resume;
  },
  async pauseChasing(shortCode, until) {
    let body = {
      query: `mutation {
        pause {
          ShortCode
        }
      }`,
    };
    if (until) {
      body = {
        query: `mutation pause ($until: Date!) {
          pause (until: $until) {
            ShortCode
          }
        }`,
        variables: {
          until,
        },
      };
    }

    const { data: result } = await ApiService.orgEndpoint(shortCode, {
      extractResponseData: false,
    }).post("/", { ...body });
    const { data, errors } = result;
    if (errors && errors.length) {
      throw errors;
    }
    return data.resume;
  },
  async setThreshold({
    updateContactsUsingDefaultThreshold,
    updateAllContacts,
    threshold,
    ShortCode,
  }) {
    const body = {
      query: `mutation setOrganisationThreshold($updateContactsUsingDefaultThreshold: Boolean!, $updateAllContacts: Boolean!, $threshold: ThresholdInputType!) {
        setOrganisationThreshold(updateContactsUsingDefaultThreshold: $updateContactsUsingDefaultThreshold, updateAllContacts: $updateAllContacts, threshold: $threshold) {
          ShortCode
        }
      }
      `,
      variables: {
        updateContactsUsingDefaultThreshold,
        threshold,
        updateAllContacts,
      },
    };

    await ApiService.orgEndpoint(ShortCode).post("/", body);
  },
  async setEarlyPaymentDiscount({ earlyPaymentDiscountValues, ShortCode }) {
    const body = {
      query: `mutation setEarlyPaymentDiscount($earlyPaymentDiscountValues: EarlyPaymentDiscountInput!) {
        setEarlyPaymentDiscount(earlyPaymentDiscountValues: $earlyPaymentDiscountValues) {
          ShortCode
        }
      }
      `,
      variables: {
        earlyPaymentDiscountValues,
      },
    };

    await ApiService.orgEndpoint(ShortCode).post("/", body);
  },
  async setLatePaymentFees(data) {
    const body = {
      query: `mutation setLatePaymentFees($latePaymentFeesValues: LatePaymentFeesInputType!) {
        setLatePaymentFees(latePaymentFeesValues: $latePaymentFeesValues) {
          ShortCode
        }
      }
      `,
      variables: {
        latePaymentFeesValues: data.values.data,
      },
    };

    await ApiService.orgEndpoint(data.ShortCode).post("/", body);
  },
  async unsetEarlyPaymentDiscount({ ShortCode }) {
    const body = {
      query: `mutation unsetEarlyPaymentDiscount {
        unsetEarlyPaymentDiscount {
          ShortCode
        }
      }
      `,
    };

    await ApiService.orgEndpoint(ShortCode).post("/", body);
  },
  async unsetLatePaymentFees({ ShortCode }) {
    const body = {
      query: `mutation unsetLatePaymentFees {
        unsetLatePaymentFees {
          ShortCode
        }
      }
      `,
    };

    await ApiService.orgEndpoint(ShortCode).post("/", body);
  },
  async removeThreshold({ removeThresholdFromCustomers, ShortCode }) {
    const body = {
      query: `mutation unsetOrganisationThreshold($removeThresholdFromCustomers: Boolean!) {
        unsetOrganisationThreshold(removeThresholdFromCustomers: $removeThresholdFromCustomers) {
          ShortCode
        }
      }
      `,
      variables: {
        removeThresholdFromCustomers,
      },
    };

    await ApiService.orgEndpoint(ShortCode).post("/", body);
  },
  async verifySender({ token, ShortCode }) {
    const body = {
      query: `mutation verifySender ($token: String!, $ShortCode: String!) {
        verifySender(ShortCode: $ShortCode, token: $token)
      }`,
      variables: {
        ShortCode,
        token,
      },
    };

    const data = await ApiService.baseEndpoint().post("/", body);
    return data;
  },
  sync(ShortCode, automaticFirstSync = false) {
    const body = {
      query: `mutation refreshOrganisation ($automaticFirstSync: Boolean) {
        refreshOrganisation (automaticFirstSync: $automaticFirstSync)
      }`,
      variables: {
        automaticFirstSync,
      },
    };
    return ApiService.orgEndpoint(ShortCode).post("/", body);
  },
  calculateDSO(ShortCode) {
    const body = {
      query: `mutation {
        calculateDSO
      }`,
    };
    return ApiService.orgEndpoint(ShortCode).post("/", body);
  },
  calculateCustomerReport(ShortCode) {
    const body = {
      query: `mutation {
        calculateCustomerReport
      }`,
    };
    return ApiService.orgEndpoint(ShortCode).post("/", body);
  },
  async getLastChase(shortCode) {
    const body = {
      query: `{
        organisation {
          chaseFeed {
            chasing {
              lastChase {
                date
              }
            }
          }
        }
      }`,
    };
    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return get(data, "organisation.chaseFeed.chasing.lastChase");
  },
  async getUpdating(shortCode) {
    const body = {
      query: `{
        organisation {
          updating
        }
      }`,
    };
    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return get(data, "organisation.updating", false);
  },
  async getPendingPayment({ paymentId, ShortCode }) {
    const body = {
      query: `query($paymentId: String!) {
        organisation {
          pendingPayment(paymentId: $paymentId) {
            invoices {
              _id
              InvoiceID
              FullyPaidOnDate
              InvoiceNumber
              AmountPaid
              Total
              Contact {
                Name
                ContactID
              }
            }
            currency
            amount
          }
        }
      }`,
      variables: {
        paymentId,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.organisation.pendingPayment;
  },
  async revenueForecast({ ShortCode, startDate }) {
    const body = {
      query: `query($startDate: Date) {
        organisation {
          revenueForecast(startDate: $startDate) {
            totalAvailableMonths
            createdAt
            actuals {
              date
              amount
            }
            predictions {
              date
              amount
              predictedPaymentAmountUpper
              predictedPaymentAmountLower
            }
          }
        }
      }`,
      variables: {
        startDate,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);

    return data.organisation.revenueForecast;
  },
  async revenueForecastStats({ ShortCode, forecastingQuery, forecastingName }) {
    const body = {
      query: `{
        organisation {
          revenueForecastStats {
            ${forecastingQuery}
          }
        }
      }`,
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);
    return data.organisation.revenueForecastStats[forecastingName];
  },
  async resetLastRefreshDate(ShortCode) {
    const body = {
      query: `mutation {
        resetLastRefreshDate
      }`,
    };
    return ApiService.orgEndpoint(ShortCode).post("/", body);
  },

  async getAuthCertificateById({ ShortCode, certificateId }) {
    const body = {
      query: `query($certificateId: String!) {
        organisation {
          authCertificates(certificateId: $certificateId) {
            certificateId
            accountId
            validUntil
            createdAt
            isActive
          }
        }
      }`,
      variables: {
        certificateId,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post("/", body);

    return data.organisation.authCertificates?.[0];
  },
};

export default OrganisationService;
export { FORECASTING_QUERIES };
